import { createTypedReducerWithImmer } from "@insite/client-framework/Common/CreateTypedReducer";
import siteMessage from "@insite/client-framework/SiteMessage";
import { isExpiryDatePassed } from "@WhiteCap/Common/Utillities/utils";
import {
    BranchContactModel,
    CalculationMethod,
    ContactModel,
    GetQuoteApiResultModel,
    QuoteLineModel,
} from "@WhiteCap/Store/Service/OsnQuotes/OsnQuotesApiModels";
import { Draft } from "immer";
import cloneDeep from "lodash/cloneDeep";

export interface OsnCreateQuoteDataModel {
    isLoading: boolean;
    quoteData: null | GetQuoteApiResultModel;
    discountBy: {
        type: string;
        value: string;
    };
    editedFields: {
        quoteName: string;
        deliveryMethod: string;
        billContact: ContactModel;
        shipContact: ContactModel;
        customerPo: string;
        expirationDate?: Date | null;
        branchContact: BranchContactModel;
    };
    quoteLineErrors: {
        [quoteLineId: string]: string[];
    };
    editableFieldsErrors: {
        [key: string]: string;
    };
    isSubmitted: boolean;
    isSubmittingQuote: boolean;
    subTotal: number;
    mobileTabActiveIndex: number;
    isMobile: boolean;
    isJobsiteUpdateWarningModalOpen: boolean;
    isAddingQuoteLine: boolean;
}

const initialState: OsnCreateQuoteDataModel = {
    isLoading: false,
    quoteData: null,
    discountBy: {
        type: "",
        value: "",
    },
    editedFields: {
        quoteName: "",
        deliveryMethod: "",
        billContact: {
            firstName: "",
            lastName: "",
            email: "",
        },
        shipContact: {
            firstName: "",
            lastName: "",
            email: "",
        },
        customerPo: "",
        branchContact: {
            Name: "",
            Value: "",
        },
    },
    quoteLineErrors: {},
    editableFieldsErrors: {},
    isSubmitted: false,
    isSubmittingQuote: false,
    subTotal: 0,
    mobileTabActiveIndex: 0,
    isMobile: false,
    isJobsiteUpdateWarningModalOpen: false,
    isAddingQuoteLine: false,
};

const calculatePriceByDiscount = (
    discountBy: string,
    value: string,
    quoteLine: QuoteLineModel,
    calculationMethodExistsOnQuoteLine: boolean,
    calculationMethod: CalculationMethod,
) => {
    if (!quoteLine.pricingRfq || !quoteLine.pricingRfq.calculationMethods) {
        return {
            discountedPrice: 0,
            error: "",
        };
    }
    let discountedPrice: number = quoteLine.quotePrice;
    let error = "";
    // const calculationMethodIndex = quoteLine.pricingRfq.calculationMethods.findIndex(
    //     calculationMethod => calculationMethod.name === discountBy,
    // );

    switch (discountBy) {
        case "List":
            if (calculationMethodExistsOnQuoteLine) {
                discountedPrice = +(
                    quoteLine.pricingRfq.listPrice -
                    (+value / 100) * quoteLine.pricingRfq.listPrice
                ).toFixed(2);
                error =
                    quoteLine.pricingRfq.minimumPriceAllowed > discountedPrice
                        ? `${siteMessage("OSN_EditQuote_Quote_Line_Min_Price_Allowed_Message")
                            .toString()
                            .replace("{{minimumPriceAllowed}}", quoteLine.pricingRfq.minimumPriceAllowed.toString())}`
                        : "";
            }
            break;
        case "Customer":
            if (calculationMethodExistsOnQuoteLine) {
                discountedPrice = +(
                    quoteLine.pricingRfq.customerPrice -
                    (+value / 100) * quoteLine.pricingRfq.customerPrice
                ).toFixed(2);
                error =
                    quoteLine.pricingRfq.minimumPriceAllowed > discountedPrice
                        ? `${siteMessage("OSN_EditQuote_Quote_Line_Min_Price_Allowed_Message")
                            .toString()
                            .replace("{{minimumPriceAllowed}}", quoteLine.pricingRfq.minimumPriceAllowed.toString())}`
                        : "";
            }
            break;
        case "unit":
            if (calculationMethodExistsOnQuoteLine) {
                discountedPrice = +(quoteLine.pricingRfq.unitCost / (1 - +value / 100)).toFixed(2);
                error =
                    quoteLine.pricingRfq.minimumPriceAllowed > discountedPrice
                        ? `${siteMessage("OSN_EditQuote_Quote_Line_Min_Price_Allowed_Message")
                            .toString()
                            .replace("{{minimumPriceAllowed}}", quoteLine.pricingRfq.minimumPriceAllowed.toString())}`
                        : "";
            }
            break;
        default:
            discountedPrice = quoteLine.quotePrice;
            error = "";
    }

    return {
        discountedPrice,
        error,
    };
};

const getDeliveryMethod = (shippingMethod: string, deliveryTypes?: string[]) => {
    if (!deliveryTypes || deliveryTypes.length === 0) {
        return "";
    }
    const selectedDeliveryTypeMethodIndex =
        // eslint-disable-next-line array-callback-return
        deliveryTypes?.findIndex(type => {
            const method = shippingMethod;
            const methodTextFromType = type.split("-")[0];
            if (method && methodTextFromType.toLowerCase() === method.toLowerCase()) {
                return true;
            }
        });

    let deliveryType = "";
    if (selectedDeliveryTypeMethodIndex === 0 || selectedDeliveryTypeMethodIndex) {
        deliveryType = deliveryTypes[selectedDeliveryTypeMethodIndex];
    }
    return deliveryType;
};

const osnQuoteListReducer = {
    // Get Quote
    "Custom/OsnQuote/BeginGetQuoteDataLoad": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isLoading = true;
    },
    "Custom/OsnQuote/GetQuoteDataLoadError": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isLoading = false;
    },
    "Custom/OsnQuote/CompleteGetQuoteDataLoad": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            result: GetQuoteApiResultModel;
        },
    ) => {

        action.result.quoteLineCollection?.forEach(quoteLine => {
            const splittedArr = quoteLine.quotePriceDisplay ? quoteLine.quotePriceDisplay.slice(1).trim().split(".") : [];
            // Add zeros if required
            if (splittedArr.length === 1) {
                quoteLine.quotePriceDisplay = `$${splittedArr[0]}.00`;
            } else if (splittedArr.length === 2) {
                const zerosToBeAdded = splittedArr[1].length === 1 ? "0" : splittedArr[1].length === 0 ? "00" : "";
                if (zerosToBeAdded) {
                    quoteLine.quotePriceDisplay = `$${splittedArr[0]}.${splittedArr[1]}${zerosToBeAdded}`;
                }
            }
        });
        draft.quoteData = action.result;
        draft.isSubmitted = false;

        // Bill Contact
        const billToContact = action.result.contacts?.find(item => item.contactId === action.result.billToContactId);
        const billContact = {
            firstName: billToContact ? billToContact.firstName : "",
            lastName: billToContact ? billToContact.lastName : "",
            contactId: billToContact ? billToContact.contactId : "",
        };

        // Ship Contact
        const shipToContact = action.result.contacts?.find(item => item.contactId === action.result.shipToContactId);
        const shipContact = {
            firstName: shipToContact ? shipToContact.firstName : "",
            lastName: shipToContact ? shipToContact.lastName : "",
            contactId: shipToContact ? shipToContact.contactId : "",
        };

        // Delivery Method
        const deliveryMethod = action.result.shippingMethod
            ? getDeliveryMethod(action.result.shippingMethod, action.result.deliveryType)
            : "";

        draft.editedFields = {
            quoteName: action.result.quoteName || "",
            deliveryMethod,
            billContact,
            shipContact,
            customerPo: action.result.poNumber || "",
            expirationDate: action.result.quoteExpiryDate ? new Date(action.result.quoteExpiryDate) : null,
            branchContact: {
                Name: action.result.salesRepName || "",
                Value: action.result.salesRepEmail || "",
            },
        };

        // Set errors
        draft.editableFieldsErrors = {
            quoteName: action.result.quoteName
                ? ""
                : siteMessage("OSN_EditQuote_Quote_Name_Required_Message").toString(),
            deliveryMethod: action.result.shippingMethod
                ? ""
                : siteMessage("OSN_EditQuote_Delivery_Method_Required_Message").toString(),
            customerPo: action.result.poNumber
                ? ""
                : siteMessage("OSN_EditQuote_Customer_PO_Required_Message").toString(),
            expirationDate: action.result.quoteExpiryDate
                ? isExpiryDatePassed(new Date(action.result.quoteExpiryDate), new Date())
                    ? siteMessage("OSN_EditQuote_Expiration_Date_Not_Valid_Message").toString()
                    : ""
                : siteMessage("OSN_EditQuote_Expiration_Date_Required_Message").toString(),
            shipContact: action.result.shipToContact
                ? ""
                : siteMessage("OSN_EditQuote_Ship_Contact_Required_Message").toString(),
            billContact: action.result.billToContact
                ? ""
                : siteMessage("OSN_EditQuote_Bill_Contact_Required_Message").toString(),
        };

        draft.quoteLineErrors = {};

        draft.isLoading = false;
    },
    "Custom/OsnCreateQuote/UpdateQuoteName": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: string;
            error: string;
        },
    ) => {
        draft.editedFields.quoteName = action.value;
        draft.editableFieldsErrors.quoteName = action.error;
    },
    "Custom/OsnCreateQuote/UpdateDeliveryMethod": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: string;
            error: string;
        },
    ) => {
        draft.editedFields.deliveryMethod = action.value;
        draft.editableFieldsErrors.deliveryMethod = action.error;
    },
    "Custom/OsnCreateQuote/UpdateBillContact": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: ContactModel;
            error: string;
        },
    ) => {
        draft.editedFields.billContact = action.value;
        draft.editableFieldsErrors.billContact = action.error;
    },
    "Custom/OsnCreateQuote/UpdateShipContact": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: ContactModel;
            error: string;
        },
    ) => {
        draft.editedFields.shipContact = action.value;
        draft.editableFieldsErrors.shipContact = action.error;
    },
    "Custom/OsnCreateQuote/UpdateCustomerPo": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: string;
            error: string;
        },
    ) => {
        draft.editedFields.customerPo = action.value;
        draft.editableFieldsErrors.customerPo = action.error;
    },
    "Custom/OsnCreateQuote/UpdateExpirationDate": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: Date;
            error: string;
        },
    ) => {
        draft.editedFields.expirationDate = action.value;
        draft.editableFieldsErrors.expirationDate = action.error;
    },
    "Custom/OsnCreateQuote/UpdateBranchContact": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: BranchContactModel;
            error: string;
        },
    ) => {
        draft.editedFields.branchContact = action.value;
        draft.editableFieldsErrors.branchContact = action.error;
    },
    "Custom/OsnCreateQuote/UpdateQuoteLinePrice": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: number;
            id: string;
            error: string;
        },
    ) => {
        if (!draft.quoteData?.quoteLineCollection) {
            return;
        }
        const quoteLineToUpdate =
            draft.quoteData?.quoteLineCollection?.filter(quoteLine => quoteLine.product.erpNumber === action.id) || [];
        if (quoteLineToUpdate.length === 0) {
            return;
        }
        quoteLineToUpdate[0].quotePrice = action.value;
        const updatedQuoteData = { ...draft.quoteData };
        updatedQuoteData.quoteLineCollection?.map(item => {
            return item.product.erpNumber === action.id ? quoteLineToUpdate : item;
        });
        draft.quoteData = updatedQuoteData;
        draft.quoteLineErrors[quoteLineToUpdate[0].product.erpNumber] = [
            action.error,
            draft.quoteLineErrors[quoteLineToUpdate[0].product.erpNumber]
                ? draft.quoteLineErrors[quoteLineToUpdate[0].product.erpNumber][1]
                : "",
        ];
    },
    "Custom/OsnCreateQuote/UpdateQuoteLineQty": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: number;
            id: string;
            error: string;
        },
    ) => {
        if (!draft.quoteData?.quoteLineCollection) {
            return;
        }
        const quoteLineToUpdate =
            draft.quoteData?.quoteLineCollection?.filter(quoteLine => quoteLine.product.erpNumber === action.id) || [];
        if (quoteLineToUpdate.length === 0) {
            return;
        }
        quoteLineToUpdate[0].qty = action.value;

        const updatedQuoteData = { ...draft.quoteData };
        updatedQuoteData.quoteLineCollection?.map(item => {
            return item.product.erpNumber === action.id ? quoteLineToUpdate : item;
        });
        draft.quoteData = updatedQuoteData;
        // Set quote line error
        draft.quoteLineErrors[quoteLineToUpdate[0].product.erpNumber] = [
            draft.quoteLineErrors[quoteLineToUpdate[0].product.erpNumber]
                ? draft.quoteLineErrors[quoteLineToUpdate[0].product.erpNumber][0]
                : "",
            action.error,
        ];
    },
    "Custom/OsnCreateQuote/AddQuoteLine": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            quoteLine: QuoteLineModel;
        },
    ) => {
        if (!draft.quoteData) {
            draft.quoteData = {
                ...initialState.quoteData!,
                quoteLineCollection: [action.quoteLine],
            };
        } else if (!draft.quoteData.quoteLineCollection) {
            draft.quoteData.quoteLineCollection = [action.quoteLine];
        } else {
            draft.quoteData.quoteLineCollection.push(action.quoteLine);
        }
    },
    "Custom/OsnCreateQuote/IsAddingQuoteLine": (
        draft: Draft<OsnCreateQuoteDataModel>, action: {
            value: boolean;
        },
    ) => {
        draft.isAddingQuoteLine = action.value;
    },
    "Custom/OsnCreateQuote/RemoveQuoteLine": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            id: string;
        },
    ) => {
        if (!draft.quoteData?.quoteLineCollection) {
            return;
        }
        const updatedQuoteLines: QuoteLineModel[] = [];
        let maxEbsLineNumber = 0;

        // filter and separate EBS and non-EBS quote lines
        for (const quoteLine of draft.quoteData.quoteLineCollection) {
            if (quoteLine.product.erpNumber === action.id) {
                if (quoteLine.isEbsQuoteLine) {
                    quoteLine.isDeleted = true;
                    updatedQuoteLines.push(quoteLine);
                    maxEbsLineNumber = Math.max(maxEbsLineNumber, quoteLine.lineNumber);
                }
                // Skip non-EBS line — this is being removed
            } else {
                updatedQuoteLines.push(quoteLine);
                if (quoteLine.isEbsQuoteLine) {
                    maxEbsLineNumber = Math.max(maxEbsLineNumber, quoteLine.lineNumber);
                }
            }
        }

        // Reassign line numbers to non-EBS quote lines only
        let currentLineNumber = maxEbsLineNumber + 1;
        for (const quoteLine of updatedQuoteLines) {
            if (!quoteLine.isEbsQuoteLine && !quoteLine.isDeleted) {
                quoteLine.lineNumber = currentLineNumber++;
            }
        }

        const updatedQuoteData = { ...draft.quoteData };
        updatedQuoteData.quoteLineCollection = updatedQuoteLines;
        draft.quoteData = updatedQuoteData;
        // Remove associated quoteline error(s)
        const errorsObj = cloneDeep(draft.quoteLineErrors);
        delete errorsObj[action.id];
        draft.quoteLineErrors = errorsObj;
    },
    "Custom/OsnCreateQuote/UpdateDiscountBy": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            discountBy: string;
            value: string;
        },
    ) => {
        if (!draft.quoteData?.quoteLineCollection) {
            return;
        }

        const updatedQuoteLines = draft.quoteData?.quoteLineCollection?.map(quoteLine => {
            const calculationMethodIndex = quoteLine.pricingRfq.calculationMethods.findIndex(
                method => method.name === action.discountBy,
            );
            const calculationMethodExistsOnQuoteLine = calculationMethodIndex !== -1;

            if (calculationMethodExistsOnQuoteLine) {
                const calculationMethod = quoteLine.pricingRfq.calculationMethods[calculationMethodIndex];
                // Get discounted quote price
                const result = calculatePriceByDiscount(
                    action.discountBy,
                    action.value,
                    quoteLine,
                    calculationMethodExistsOnQuoteLine,
                    calculationMethod,
                );
                quoteLine.quotePrice = result.discountedPrice;
                // Set quote line error
                draft.quoteLineErrors[quoteLine.product.erpNumber] = [
                    result.error,
                    draft.quoteLineErrors[quoteLine.product.erpNumber]
                        ? draft.quoteLineErrors[quoteLine.product.erpNumber][1]
                        : "",
                ];
            }
            return quoteLine;
        });

        const updatedQuoteData = { ...draft.quoteData };
        updatedQuoteData.quoteLineCollection = updatedQuoteLines;
        draft.quoteData = updatedQuoteData;
    },
    "Custom/OsnCreateQuote/UpdateIsSubmitted": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: boolean;
        },
    ) => {
        draft.isSubmitted = action.value;
    },

    "Custom/OsnCreateQuote/UpdateSubTotal": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: number;
        },
    ) => {
        draft.subTotal = action.value;
    },

    "Custom/OsnCreateQuote/UpdateJobSiteModalOpenState": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: boolean;
        },
    ) => {
        draft.isJobsiteUpdateWarningModalOpen = action.value;
    },

    // Create Quote
    "Custom/OsnQuote/BeginSubmitCreateQuote": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isSubmittingQuote = true;
    },
    "Custom/OsnQuote/CreateQuoteSubmitError": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isSubmittingQuote = false;
    },
    "Custom/OsnQuote/CompleteSubmitCreateQuote": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isSubmittingQuote = false;
        draft = initialState;
    },

    "Custom/OsnQuote/UpdateIsMobile": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: boolean;
        },
    ) => {
        draft.isMobile = action.value;
    },
    "Custom/OsnQuote/UpdateMobileActiveTabIndex": (
        draft: Draft<OsnCreateQuoteDataModel>,
        action: {
            value: number;
        },
    ) => {
        draft.mobileTabActiveIndex = action.value;
    },
    // Update Quote
    "Custom/UpdateQuote/BeginUpdatingQuote": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isSubmittingQuote = true;
    },
    "Custom/UpdateQuote/UpdateQuoteError": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isSubmittingQuote = false;
    },
    "Custom/UpdateQuote/CompleteUpdatingQuote": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft.isSubmittingQuote = false;
        draft = initialState;
    },
    // Reset to initial state
    "Custom/OsnQuote/ReinitializeState": (draft: Draft<OsnCreateQuoteDataModel>) => {
        draft = initialState;
    },
};

export default createTypedReducerWithImmer(initialState, osnQuoteListReducer);
